import { RecipesDocument, type RecipesInput, type RecipesQuery } from '@mdvip/shared/graphql/generated/graphql';
import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export type RecipesQueryResult = RecipesQuery['recipes'];
export type Recipe = RecipesQueryResult['edges'][number];

export type UseRecipesInput = Omit<RecipesInput, 'offset' | 'limit'> & { offset: number; limit: number };

/** Prefix for invalidating every recipe list page at once — no per-input suffix, mirrors savedPaymentMethodsKey. */
export const recipesKey = ['recipes', 'list'] as const;

/**
 * One page of the recipe catalog, newest first. `keepPreviousData` holds the previous page on
 * screen while the next one loads, so paging swaps cards in place instead of flashing the skeleton.
 *
 * No `Failure` variant to handle: `recipes` is a pure read with no domain-level business-failure
 * mode — infrastructure errors (e.g. `SERVICE_UNAVAILABLE`) travel the standard GraphQL errors
 * envelope, handled by the caller via `extractGqlErrorCode`.
 */
export function useRecipes(input: UseRecipesInput) {
  return useQuery({
    queryKey: [...recipesKey, input],
    queryFn: async () => {
      const data = await gqlRequest(RecipesDocument, { input });
      return data.recipes;
    },
    placeholderData: keepPreviousData,
  });
}
